import React from 'react';

import { sanitizeUrl } from './sanitize';

import ExternalLink from '../components/ExternalLink/ExternalLink';
import { normalizeDescriptionTokens } from './richDescriptionMarkup';
// NOTE: Importing directly from file to avoid circular dependency with components/index.js

// Host free text (listing description, photo captions, amenity notes) is written in plain
// textareas whose only rich-text features are the toolbar buttons: bold, bullet list and
// hyperlink. Those buttons insert markdown-ish tokens ("**bold**", "- item", "[label](url)").
// Running a full markdown pipeline over that text reflows the host's whitespace — blank lines
// are collapsed into rebuilt paragraphs — so the published page would no longer match what the
// host typed or what the review preview shows. Instead we render the text verbatim (the
// containers keep `white-space: pre-wrap`) and only turn the supported tokens into elements.
const INLINE_TOKEN_REGEX = /\*\*([^*\n]+)\*\*|\[([^\]\n]*)\]\(([^)\s]*)\)/g;
const BULLET_LINE_REGEX = /^[ \t]*[-*•][ \t]+(.*)$/;

// A link token can end up with whitespace - a plain space, or a line break - between its
// label and its URL ("[label]\n(https://example.com)"). The token then no longer matches
// and the host's markup shows up raw on the page, so pull those halves back together
// first. Only URL-looking targets are joined, to leave ordinary "[note] (an aside)" text
// alone.
const SPLIT_LINK_TOKEN_REGEX = /\]\s+\(\s*((?:https?:\/\/|\/\/|www\.)[^\s)]+|[^\s()]+\.[a-z]{2,}[^\s)]*)\s*\)/gi;

export const joinSplitLinkTokens = text =>
  typeof text === 'string'
    ? text.replace(SPLIT_LINK_TOKEN_REGEX, (token, href) => `](${href})`)
    : text;

const trimText = value => (typeof value === 'string' ? value.trim() : '');

const hasUrlScheme = url => /^[a-z][a-z0-9+.-]*:/i.test(url || '');

const normalizeDescriptionLinkHref = href => {
  const trimmedHref = trimText(href);
  if (!trimmedHref) {
    return '';
  }

  const looksLikeDomain =
    trimmedHref &&
    !hasUrlScheme(trimmedHref) &&
    !trimmedHref.startsWith('//') &&
    !trimmedHref.startsWith('/') &&
    !trimmedHref.startsWith('#') &&
    trimmedHref.includes('.');

  const normalizedHref = looksLikeDomain
    ? `https://${trimmedHref}`
    : trimmedHref;
  const sanitizedHref = sanitizeUrl(normalizedHref);

  return sanitizedHref === 'about:blank' ? '' : sanitizedHref;
};

export const DescriptionLink = props => {
  const { href, children, ...rest } = props;
  const normalizedHref = normalizeDescriptionLinkHref(href);
  if (!normalizedHref) {
    return <span>{children}</span>;
  }

  const isExternalLink =
    hasUrlScheme(normalizedHref) || normalizedHref.startsWith('//');

  return isExternalLink ? (
    <ExternalLink {...rest} href={normalizedHref}>
      {children}
    </ExternalLink>
  ) : (
    <a {...rest} href={normalizedHref}>
      {children}
    </a>
  );
};

// A description truncated to the preview length can end in the middle of a token; drop the
// partial token so the reader never sees a raw "[Sau" fragment or a stray "**".
export const dropDanglingTokens = value => {
  if (typeof value !== 'string') {
    return value;
  }

  const text = normalizeDescriptionTokens(value);
  const withoutDanglingLink = text.replace(/\[[^\]\n]*(\][^)\s]*)?$/, '');
  const boldMarkerCount = (withoutDanglingLink.match(/\*\*/g) || []).length;
  return boldMarkerCount % 2 === 0
    ? withoutDanglingLink
    : withoutDanglingLink.replace(/\s*\*\*[^*\n]*$/, '');
};

// Bold + link tokens inside a single line of text.
//
// The two features nest: a bold link is stored as "**[label](url)**" and a link with bold
// text as "[**label**](url)". The outer token is matched first, so its contents run through
// this same function again - otherwise the bold body would swallow the link token and the
// host's raw markup would end up on the page. The regex instance is local because those
// recursive calls share the pattern.
const renderInlineTokens = (text, keyPrefix) => {
  const nodes = [];
  const tokenRegex = new RegExp(INLINE_TOKEN_REGEX.source, 'g');
  let lastIndex = 0;
  let match;
  while ((match = tokenRegex.exec(text)) !== null) {
    const [token, boldText, linkLabel, linkHref] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (boldText != null) {
      const boldKey = `${keyPrefix}-b-${match.index}`;
      nodes.push(
        <strong key={boldKey}>{renderInlineTokens(boldText, boldKey)}</strong>
      );
    } else {
      const linkKey = `${keyPrefix}-a-${match.index}`;
      nodes.push(
        <DescriptionLink key={linkKey} href={linkHref}>
          {renderInlineTokens(linkLabel, linkKey)}
        </DescriptionLink>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

/**
 * Render host-authored text that may contain bold, bullet list and hyperlink tokens.
 *
 * @param {String} text raw text as the host typed it
 * @param {Object} options optional class names for the rendered list elements
 * @returns {Array<React.Node>|null} nodes ready to be rendered inside a pre-wrap container
 */
export const renderRichDescription = (text, options = {}) => {
  if (typeof text !== 'string' || !text) {
    return null;
  }

  const { listClassName, listItemClassName } = options;
  const lines = normalizeDescriptionTokens(text).split('\n');
  const blocks = [];

  lines.forEach(line => {
    const bulletMatch = line.match(BULLET_LINE_REGEX);
    const previousBlock = blocks[blocks.length - 1];

    if (bulletMatch) {
      if (previousBlock?.type === 'list') {
        previousBlock.items.push(bulletMatch[1]);
      } else {
        blocks.push({ type: 'list', items: [bulletMatch[1]] });
      }
    } else if (previousBlock?.type === 'text') {
      previousBlock.lines.push(line);
    } else {
      blocks.push({ type: 'text', lines: [line] });
    }
  });

  return blocks.map((block, blockIndex) => {
    if (block.type === 'list') {
      return (
        <ul className={listClassName} key={`list-${blockIndex}`}>
          {block.items.map((item, itemIndex) => (
            <li className={listItemClassName} key={`item-${blockIndex}-${itemIndex}`}>
              {renderInlineTokens(item, `${blockIndex}-${itemIndex}`)}
            </li>
          ))}
        </ul>
      );
    }

    // A text block that only exists to separate two lists would render as an empty line;
    // keep it out so the host's list spacing is not doubled.
    const blockText = block.lines.join('\n');
    const isSeparatorOnly =
      !blockText.trim() && blockIndex > 0 && blockIndex < blocks.length - 1;
    if (isSeparatorOnly) {
      return null;
    }

    return (
      <React.Fragment key={`text-${blockIndex}`}>
        {renderInlineTokens(blockText, `${blockIndex}`)}
      </React.Fragment>
    );
  });
};
